import { Component, OnInit } from '@angular/core';
import { WebUserFilterComponent } from './web-user-filter/web-user-filter.component';
import { DialogService, LanguageService, HelperService } from 'src/app/services';
import { MAINTENANCE_WEBUSER_FOLDERNAME } from 'src/app/utils/constant';
import { environment } from 'src/environments/environment';
import { requestWebUser } from 'src/app/model/requests';
import { of as observableOf, Subscription } from 'rxjs';
import { MediaObserver } from '@angular/flex-layout';
import { catchError, finalize } from 'rxjs/operators';
import { WebUserService } from './web-user.service';
import { MatDialogRef } from '@angular/material/dialog';
import * as _ from 'lodash';

interface Role {
  role_id?: number;
  role_code?: string;
  role_description?: string;
  role?: {
    id: number | null;
    code: string | null;
    description: string | null;
  };
}

@Component({
  selector: 'esc-web-user',
  templateUrl: './web-user.component.html',
  styleUrls: ['./web-user.component.scss']
})

export class WebUserComponent implements OnInit {
  public loading = {
    init: false,
    detail: false,
    more: false,
    export: false,
    import: false,
    error: false,
    detailError: false,
  }
  private listSub: Subscription;
  private detailSub: Subscription;
  public folderName = MAINTENANCE_WEBUSER_FOLDERNAME;
  public listData = [];
  public detailData;
  public columns = ["username", "fullname", "is_active"];
  public excelCols = ["USERNAME", "FULLNAME", "EMAIL", "ROLE CODE", "ROLE NAME", "STATUS"];
  public detailKeys = ["username", "fullname", "email_address", "role", "web_user_company", "is_active"];
  private currentFilters;
  private previousFilters;
  public listLength = 0;
  public mode;
  public modifying: Boolean = false;
  public deselect: Boolean = false;
  networkErrMsg = this.languageService.getTranslation("MSG_ERROR_NETWORK_CONNECTION", {});

  constructor(
    public media: MediaObserver,
    private webUserSvc: WebUserService,
    private dialogService: DialogService,
    private languageService: LanguageService,
    private helper: HelperService
  ) { }

  ngOnInit() {
    requestWebUser.web_filter.role_code = localStorage.getItem("userRole");
    this.currentFilters = _.cloneDeep(requestWebUser);
    this.currentFilters.web_filter.role_code = null; // Default to null for ALL, handle appropriately in HOST API
    this.getListData();
    this.initColumns();
  }
  
  ngOnDestroy() {
    if (this.listSub && !this.listSub.closed) {
      this.listSub.unsubscribe();
    }
  }

  getListData() {
    if (this.listSub && !this.listSub.closed) {
      this.listSub.unsubscribe();
    }
    if (this.loading.init == true) {
      return;
    }
    this.loading.error = false;
    if (this.listData.length == 0) {
      this.loading.init = true;
    } else {
      this.loading.more = true;
    }
    this.listSub = this.webUserSvc.getList(this.currentFilters).pipe(
      finalize(() => {
        this.loading.init = false;
        this.loading.more = false;
        return observableOf(null);
      }),
      catchError((err) => {
        if (this.listData.length == 0) {
          this.loading.error = true;
        } else {
          this.dialogService.openSnackBar(this.languageService.getTranslation("MSG_INTERNAL_SERVER_ERROR", {}));
          this.currentFilters.web_filter.offset -= this.currentFilters.web_filter.limit;
        }
        return observableOf(null);
      }))
      .subscribe((res: any) => {
        if (environment.showLogs) {
          console.log(res);
        }
        if (!res || res.status == 0 || res.status == 504 || res.status == 500) {
          this.loading.error = true;
          this.listData = [];
          this.listLength = 0;
          this.dialogService.openSnackBar(this.networkErrMsg);
          return;
        }
        if (_.has(res, 'value') && (_.toLower(res.value) == _.toLower("Success"))) {
          this.listData = this.listData.concat(res.data);
          this.listData.forEach((e: Role) => {
            if(!_.has(e, 'role')) {
              e['role'] = {
                id: _.get(e, 'role_id', null),
                code: _.get(e, 'role_code', null),
                description: _.get(e, 'role_description', null),
              }
            }
          })
          this.listLength = _.toNumber(_.get(res, "count"));
        }
      });
  }

  // REWARDS-1341
  // If not in global schema, we will display web_user_role.is_active instead of web_user.is_active
  private initColumns() {
    const isActive = this.helper.isGlobalSchema() ? "is_active" : "web_user_role_is_active";
    this.columns = ["username", "fullname", isActive];
    this.detailKeys = ["username", "fullname", "email_address", "role", "web_user_company", isActive];
  }

  download(ev) {
    let dialogRef = this.dialogService.loading();
    this.listSub = this.webUserSvc.genericMaintenanceDownload(this.currentFilters).pipe(
      finalize(() => {
        this.loading.init = false;
        this.loading.more = false;
        return observableOf(null);
      }),
      catchError((err) => {
        if (this.listData.length == 0) {
          this.loading.error = true;
        } else {
          this.dialogService.openSnackBar(this.languageService.getTranslation("MSG_INTERNAL_SERVER_ERROR", {}));
          this.currentFilters.web_filter.offset -= this.currentFilters.web_filter.limit;
        }
        return observableOf(null);
      }))
      .subscribe((res: any) => {
        if (environment.showLogs) {
          console.log(res);
        }
        if (!res || res.status == 0 || res.status == 504 || res.status == 500) {
          this.loading.error = true;
          this.listData = [];
          this.listLength = 0;
          this.dialogService.openSnackBar(this.networkErrMsg);
          return;
        }
        if (_.has(res, 'value') && (_.toLower(res.value) == _.toLower("Success"))) {
          this.helper.openDownloadMenu(res.data);
          dialogRef.close();
        }
      });
  }

  getMore(event) {
    if (this.loading.more || this.loading.init || this.listData.length >= this.listLength) {
      return;
    }
    if (event) {
      this.currentFilters.web_filter.offset += this.currentFilters.web_filter.limit;
      this.getListData();
    }
  }

  getDetail(data) {
    if(data) {
      let temp = this.listData.filter(e => {
        return e.id == data.id;
      })
      this.detailData = _.clone(temp[0]);
      this.deselect = false;
    }
  }

  addNew(event) {
    this.closeDetail(null);
    this.detailData = {
      company: undefined,
      role: undefined
    };
    this.detailData["company"] = JSON.parse(localStorage.getItem("company"));
    if(this.helper.isGlobalSchema()) {
      this.detailData["role"] = JSON.parse(localStorage.getItem("role"));
    }
    this.modifying = true;
    this.mode = 'Add';
  }

  edit(event) {
    this.modifying = true;
  }

  searchBy(keyword) {
    this.currentFilters.web_filter.keyword = _.clone(keyword);
    this.refresh(true);
  }

  openFilter(ev) {
    let dialogRef: MatDialogRef<WebUserFilterComponent>;
    dialogRef = this.dialogService.openDialog(WebUserFilterComponent, {
      width: '500px',
      autoFocus: false,
      disableClose: false,
      data: _.get(this, 'previousFilters') || _.get(this, 'currentFilters')
    });
    dialogRef.afterClosed().subscribe((selectedFilters) => {
      if(selectedFilters) {
        this.previousFilters = _.cloneDeep(selectedFilters);

        this.currentFilters.web_filter.role_code =  _.get(selectedFilters, 'role.code') != 'all' ?_.get(selectedFilters, 'role.code') : null; // Default to null for ALL, handle appropriately in HOST API
        this.currentFilters.web_filter.status =  _.get(selectedFilters, 'status.code');
        this.refresh(null);
      }
    })
  }

  saving(event) {
    this.loading.detail = true;
    if(_.has(event[0], 'id')) {
      let obj = _.get(event, '[0]');
      this.detailData = _.clone(obj);
    } 
    else if(!event) {
      this.loading.detail = false;
    }
  }

  closeDetail(event) {
    this.detailData = null;
    this.deselect = true;
  }

  closeForm(event) {
    this.modifying = false;
    this.mode = null;
    if(event) {
      this.refresh(event);
    } else {
      this.closeDetail(event);
    }
  }

  refresh(event) {
    if (!event) {
      this.closeDetail(null);
    }
    this.listData = [];
    this.listLength = 0;
    this.currentFilters.web_filter.offset = _.clone(requestWebUser.web_filter.offset);
    this.getListData();
  }
}
